import React, { useMemo, useState } from 'react';
import type { DateRange } from '../../types/dashboard';
import type { TerritoryRow } from '../../utils/territoryRows';
import { Panel } from '../ui/Panel';
import { SortableTh } from '../ui/SortableTh';
import { ExportButton, SearchInput, ToggleSwitch } from '../ui/TableControls';
import { StatusBadge } from '../ui/StatusBadge';
import { PendingNote } from '../ui/PendingNote';
import { EmptyState } from '../ui/EmptyState';
import { useSort } from '../../hooks/useSort';
import { statusRules } from '../../utils/status';
import { monthLabel } from '../../utils/calendar';
import { formatChange, formatValue, isCountMetric, metricMeta, type TrendMetric } from '../../utils/metricConfig';
import { downloadCsv } from '../../utils/csv';
type Key = 'name' | 'coach' | 'value' | 'monthly' | 'bench' | 'gap' | 'mom' | 'yoy' | 'status';
const STATUS_ORDER = {
  'Priority review': 0,
  'Below network': 1,
  Ramping: 2,
  'On par': 3,
  'Above network': 4
};
function accessor(r: TerritoryRow, k: Key): number | string | null {
  switch (k) {
    case 'name':
      return r.territory.name;
    case 'coach':
      return r.territory.fbc;
    case 'value':
      return r.value;
    case 'monthly':
      return r.monthly;
    case 'bench':
      return r.benchMonthly;
    case 'gap':
      return r.gap;
    case 'mom':
      return r.mom;
    case 'yoy':
      return r.yoy;
    case 'status':
      return STATUS_ORDER[r.status];
  }
}
interface RankingTableProps {
  rows: TerritoryRow[];
  metric: TrendMetric;
  range: DateRange;
  onOpen: (territoryId: string) => void;
}
export function RankingTable({
  rows,
  metric,
  range,
  onOpen
}: RankingTableProps) {
  const [query, setQuery] = useState('');
  const [belowOnly, setBelowOnly] = useState(false);
  const meta = metricMeta(metric);
  const count = isCountMetric(metric);
  const fmt = (v: number) => formatValue(metric, v);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter(r => (!belowOnly || r.gap < 0) && (!q || [r.territory.name, r.territory.state, r.territory.ad ?? '', r.territory.fbc].some(s => s.toLowerCase().includes(q))));
  }, [rows, query, belowOnly]);
  const {
    sorted,
    sort,
    onSort
  } = useSort<TerritoryRow, Key>(filtered, {
    key: 'gap',
    dir: 'asc'
  }, accessor);
  const maxAbs = Math.max(0.01, ...rows.map(r => Math.abs(r.gap)));
  const totalLabel = metric === 'members' ? 'New members (period)' : count ? 'Total (period)' : `${meta.label} (period)`;
  const benchLabel = count ? 'Network avg / terr. / mo' : 'Network (period)';
  const momLabel = `MoM · ${monthLabel(range.end)}`;
  const changeCell = (v: number | null) => v === null ? <span className="text-ink-subtle" data-id="element-362" data-mp="265l93ig12vvg">—</span> : <span className={v < 0 ? 'text-negative' : 'text-positive'} data-id="element-363" data-mp="214e23llpm0pu">{formatChange(metric, v)}</span>;
  const exportCsv = () => downloadCsv(`i9-territory-${metric}.csv`, ['Territory', 'AD', 'FBC', totalLabel, ...(count ? ['Active months', 'Avg per month'] : []), benchLabel, 'Variance vs network', `MoM (${monthLabel(range.end)} vs ${monthLabel(range.end - 1)})`, 'YoY (period vs prior year)', 'Status'], sorted.map(r => [r.territory.name, r.territory.ad ?? '—', r.territory.fbc, Number(r.value.toFixed(4)), ...(count ? [r.activeMonths, Number(r.monthly.toFixed(4))] : []), Number(r.benchMonthly.toFixed(4)), formatChange(metric, r.gap), r.mom === null ? '' : formatChange(metric, r.mom), r.yoy === null ? '' : formatChange(metric, r.yoy), r.status]));
  return <Panel title="Territory performance" definitionId="performanceStatus" subtitle={count ? `${meta.label} · ${range.label} · each territory’s average per active month vs the network average per active territory per month · click a row to drill through` : `${meta.label} · ${range.label} · each territory vs the network rate for the same period · click a row to drill through`} actions={<>
          <SearchInput value={query} onChange={setQuery} placeholder="Search territory, AD or FBC" data-id="element-365" data-mp="2zczn5w72q1zk" />
          <ToggleSwitch checked={belowOnly} onChange={setBelowOnly} label="Below network avg only" data-id="element-366" data-mp="2vdz8mpvmplid" />
          <ExportButton onClick={exportCsv} data-id="element-367" data-mp="2qko8t8p8i4zy" />
        </>} bodyClassName="p-0" data-id="element-364" data-mp="2ftfrcmrynsju">
      {sorted.length === 0 ? <EmptyState title="No territories match" description={belowOnly ? 'No territory in the current scope is below the network average.' : 'Try a different search term.'} action={<button type="button" onClick={() => {
      setQuery('');
      setBelowOnly(false);
    }} className="text-xs font-semibold text-navy hover:underline" data-id="element-369" data-mp="2br1gq5obo558">
              Clear search and toggle
            </button>} data-id="element-368" data-mp="2lks6ddw6qlmi" /> : <div className="max-h-[520px] overflow-auto" data-id="element-370" data-mp="5up55bf82i35">
          <table className="w-full min-w-[1120px] border-collapse text-[12.5px]" data-id="element-371" data-mp="5te0zahv78bi">
            <thead className="sticky top-0 z-10 bg-navy-50 text-navy" data-id="element-372" data-mp="ukmpfur7daa">
              <tr data-id="element-373" data-mp="8fdcczox1n14">
                <SortableTh label="Territory" sortKey="name" sort={sort} onSort={onSort} align="left" className="pl-5" data-id="element-374" data-mp="8fwlps0i333j" />
                <SortableTh label="FBC / AD" sortKey="coach" sort={sort} onSort={onSort} align="left" data-id="element-375" data-mp="dcwre1tfu3u4" />
                <SortableTh label={totalLabel} sortKey="value" sort={sort} onSort={onSort} data-id="element-376" data-mp="n52t439hrzmi" />
                {count && <SortableTh label="Avg / month" sortKey="monthly" sort={sort} onSort={onSort} data-id="element-377" data-mp="3lmol9e77fqrj" />}
                <SortableTh label={benchLabel} sortKey="bench" sort={sort} onSort={onSort} data-id="element-378" data-mp="3qlrqayn6pwzi" />
                <SortableTh label="Variance vs network" sortKey="gap" sort={sort} onSort={onSort} data-id="element-379" data-mp="3qnmagaqmtl2j" />
                <SortableTh label={momLabel} sortKey="mom" sort={sort} onSort={onSort} data-id="element-380" data-mp="3vn8miguq4v9r" />
                <SortableTh label="YoY · period" sortKey="yoy" sort={sort} onSort={onSort} data-id="element-381" data-mp="3ez6noasep69" />
                <SortableTh label="Status" sortKey="status" sort={sort} onSort={onSort} align="left" className="pr-5" data-id="element-382" data-mp="1awq2366ssq0i" />
              </tr>
            </thead>
            <tbody className="tabular-nums" data-id="element-383" data-mp="3bvtm6lm9ragr">
              {sorted.map((r, i) => {
            const go = () => onOpen(r.territory.id);
            return <tr key={r.territory.id} tabIndex={0} onClick={go} onKeyDown={e => e.key === 'Enter' && go()} className={`cursor-pointer border-b border-line transition-colors duration-100 hover:bg-navy-50 focus-visible:bg-navy-50 focus-visible:outline-none ${i % 2 ? 'bg-canvas/50' : ''}`} data-id="element-384" data-mp="3kxazjw8aky1r" data-mpi={i}>
                    <td className="whitespace-nowrap py-2.5 pl-5 pr-3" data-id="element-385" data-mp="31z8d9p74pf7n">
                      <span className="font-semibold text-navy" data-id="element-386" data-mp="2x7yrx0sm4y1k">{r.territory.name}</span>
                      <span className="ml-1.5 text-ink-subtle" data-id="element-387" data-mp="2x65kvke38bt1">{r.territory.state}</span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-ink-muted" data-id="element-388" data-mp="24jb16pp3hggm">
                      {r.territory.fbc}
                      <span className="text-ink-subtle" data-id="element-389" data-mp="k10jl1j8zyh1"> · AD {r.territory.ad ?? '—'}</span>
                    </td>
                    <td className="px-3 py-2.5 text-right font-semibold" data-id="element-390" data-mp="ot0nf52fvbiw">{fmt(r.value)}</td>
                    {count && <td className="px-3 py-2.5 text-right" title={`${fmt(r.value)} ÷ ${r.activeMonths} active months`} data-id="element-391" data-mp="b2f3dd190vjf">
                        {fmt(r.monthly)}
                        {r.activeMonths < range.end - range.start + 1 && <span className="ml-1 text-[11px] text-ink-subtle" data-id="element-392" data-mp="13n5c55txdrm2">({r.activeMonths} mo)</span>}
                      </td>}
                    <td className="px-3 py-2.5 text-right text-ink-muted" data-id="element-393" data-mp="tq0k4pdef1xc">{fmt(r.benchMonthly)}</td>
                    <td className="px-3 py-2.5" data-id="element-394" data-mp="yp3qcd547e6w">
                      <div className="flex items-center justify-end gap-2" data-id="element-395" data-mp="yrzoa1gyq2vc">
                        <div className="relative h-2 w-24 rounded-sm bg-canvas" aria-hidden data-id="element-396" data-mp="3efzpcr326ww9">
                          <span className="absolute inset-y-0 left-1/2 w-px bg-line-strong" data-id="element-397" data-mp="3jhysbzuvzrso" />
                          <span className={`absolute inset-y-0 ${r.gap < 0 ? 'right-1/2 rounded-l-sm bg-negative/70' : 'left-1/2 rounded-r-sm bg-positive/70'}`} style={{
                      width: `${Math.abs(r.gap) / maxAbs * 50}%`
                    }} data-id="element-398" data-mp="f05pie7poftz" />
                        </div>
                        <span className={`w-16 text-right font-medium ${r.gap < 0 ? 'text-negative' : 'text-positive'}`} data-id="element-399" data-mp="3oe5mziv17f">{formatChange(metric, r.gap)}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-right" data-id="element-400" data-mp="nz98zjasix90">{changeCell(r.mom)}</td>
                    <td className="px-3 py-2.5 text-right" data-id="element-401" data-mp="nx67a209zfog">{changeCell(r.yoy)}</td>
                    <td className="py-2.5 pl-3 pr-5" data-id="element-402" data-mp="izmtf37hp4x1"><StatusBadge status={r.status} data-id="element-403" data-mp="1gfjxbffddxcz" /></td>
                  </tr>;
          })}
            </tbody>
          </table>
        </div>}
      <div className="flex flex-wrap gap-x-5 gap-y-1.5 border-t border-line px-5 py-3 text-[11.5px] text-ink-muted" data-id="element-404" data-mp="1bnipc5p8issr">
        <span className="flex items-center gap-2 font-semibold text-ink" data-id="element-405" data-mp="1jzu62096glge">
          Status for {meta.label.toLowerCase()}
          <PendingNote data-id="element-406" data-mp="1syfj5fvvr89s">Illustrative thresholds · not approved coaching standards</PendingNote>
        </span>
        {statusRules(metric).map(s => <span key={s.status} className="flex items-center gap-1.5" data-id="element-407" data-mp="1xytrz05b2m8v">
            <StatusBadge status={s.status} data-id="element-408" data-mp="22vaig0zz9cp4" />
            {s.rule}
          </span>)}
        <span className="w-full text-ink-subtle" data-id="element-409" data-mp="26x5jd4dzxo2d">
          MoM compares {monthLabel(range.end)} with {monthLabel(range.end - 1)} (latest completed month). YoY compares the full selected period with the same months one year earlier. Ramping territories (provisional 12 months from activation) stay listed and searchable; their comparison may later need a maturity-based benchmark.
        </span>
      </div>
    </Panel>;
}